import React, { useState } from 'react';
import { useLazyGetTranslationsByCategoryQuery } from '@fiji/common/src/features/localization/localizationApi';
import { useGetUserProfileQuery } from '@fiji/common/src/features/profile/profileApi';
import { ApiEndpointType, ApiResponseType } from '@fiji/common/src/features/common/commonTypes';
import i18next from '../utils/i18n';
import { Profile } from '@fiji/common/src/types';
import englishTranslations from '@fiji/common/src/translations/english';
import { DEFAULT_LANGUAGE } from '@fiji/common/src/constants/cacheTimeout';

export type UseNewCustomTranslationsResult = {
    isLoading: boolean;
    /**
     * True when not skipped and the category bundle exists for the resolved language,
     * including an installed fallback. Does not guarantee a successful fetch or that every key exists.
     */
    isReady: boolean;
};

const resolveTranslationLanguage = (profileLanguage?: string): string =>
    profileLanguage ?? window.localStorage.getItem('globalLocale') ?? DEFAULT_LANGUAGE;

export const useNewCustomTranslations = (
    category: string,
    skip?: boolean,
    languageOverride?: string
): UseNewCustomTranslationsResult => {
    const [isLoading, setIsLoading] = useState(false);
    const { data: userProfileData } = useGetUserProfileQuery(
        {},
        { skip: skip || languageOverride !== undefined }
    ) as ApiEndpointType<ApiResponseType<Profile>>;
    const [getTranslation] = useLazyGetTranslationsByCategoryQuery<any>();
    const language = languageOverride ?? resolveTranslationLanguage(userProfileData?.data?.language);
    const getCategoryTranslations = async (): Promise<void> => {
        const { data, error }: any = await getTranslation({
            body: category,
            locale: language,
        });
        if (category === 'UPCOMING_EVENTS') {
            // Adding fallback translations for Upcoming Events in case of error to avoid blank spaces in UI as this is a critical category
            i18next.addResourceBundle(
                language,
                category,
                englishTranslations[category] ?? englishTranslations.translation[category] ?? {},
                true,
                true
            );
            return;
        }
        if (!error) i18next.addResourceBundle(language, category, data?.data ?? {}, true, true);
        else
            i18next.addResourceBundle(
                language,
                category,
                englishTranslations[category] ?? englishTranslations.translation[category] ?? {},
                true,
                true
            );
    };

    React.useEffect(() => {
        let active = true;
        if (!skip && !i18next.hasResourceBundle(language, category)) {
            setIsLoading(true);
            void getCategoryTranslations().then(() => {
                if (active) setIsLoading(false);
            });
        } else {
            setIsLoading(false);
        }
        return () => {
            active = false;
        };
    }, [skip, category, language]);

    return {
        isLoading,
        isReady: !skip && i18next.hasResourceBundle(language, category),
    };
};

export const useRouteTitleTranslations = (
    profileData: ApiResponseType<Profile> | undefined,
    isAuthenticated: boolean
): UseNewCustomTranslationsResult =>
    useNewCustomTranslations(
        'ROUTE_TITLES',
        !isAuthenticated || !profileData,
        resolveTranslationLanguage(profileData?.data?.language)
    );
